import { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { UIButton, UIIcon, UISpacer, UIText } from '@/ui';
import { moonlight, red } from '@/ui/styles';
import { useLogoutAction } from '@/me/actions';
import styles from './company-suspended-mask.module.scss';
import { useCompaniesTranslation } from '../hooks';

type CompanySuspendedMaskProps = {
    /**
     * Whether this viewer belongs to another company. When they do, the block
     * offers a way back rather than stranding them: one suspended company
     * should not lock them out of the others.
     */
    canSwitchCompany: boolean;
};

/**
 * Shown in place of the whole app when the selected company is suspended.
 *
 * Replaces the app rather than covering it, so nothing behind it can be
 * clicked, tabbed to, or read by a screen reader. The two actions are the only
 * ways out — without them a viewer whose only company is suspended could not
 * even sign out.
 */
export function CompanySuspendedMask(
    props: CompanySuspendedMaskProps
): ReactElement {
    const { canSwitchCompany } = props;
    const { t } = useCompaniesTranslation();
    const navigate = useNavigate();

    const { logout, loading } = useLogoutAction();

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 72,
                        height: 72,
                        borderRadius: 999,
                        background: moonlight
                    }}
                >
                    <UIIcon name="alert-triangle" size={34} color={red} />
                </div>

                <UIText variant="h1" bold textAlign="center">
                    {t('components.company_suspended_mask.title')}
                </UIText>

                <UIText textAlign="center" type="default2">
                    {t('components.company_suspended_mask.description')}
                </UIText>

                <UIText textAlign="center" type="default2">
                    {t('components.company_suspended_mask.contact')}
                </UIText>

                <UISpacer />

                <div className={styles.actions}>
                    {canSwitchCompany && (
                        <UIButton
                            variant="primary"
                            onClick={() => {
                                void navigate('/company', { replace: true });
                            }}
                        >
                            {t(
                                'components.company_suspended_mask.switch_company'
                            )}
                        </UIButton>
                    )}
                    <UIButton
                        variant={canSwitchCompany ? 'default' : 'primary'}
                        loading={loading}
                        disabled={loading}
                        onClick={() => {
                            void logout().then(() => {
                                void navigate('/login', { replace: true });
                            });
                        }}
                    >
                        {t('components.company_suspended_mask.logout')}
                    </UIButton>
                </div>
            </div>
        </div>
    );
}
